import cx from 'classnames';
import * as _ from 'lodash';
import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { BINARY_DECOMPOSITION } from '../../../../shared/featureFlags/flagNames';
import { BillingTermType } from '../../../../shared/model-typings';
import { PREMIUM_PER_REVISION_PLAN_NAME } from '../../../../shared/subscriptions';
import { window } from '../../../globals';
import { errorMessagesListFromResponse } from '../../../helpers/errors';
import { useIsFeatureEnabled } from '../../../helpers/features';
import { useAsyncStatus } from '../../../hooks';
import * as api from '../../../redux/api';
import { BUTTON_SIZE, BUTTON_TYPE, LoaderButton } from '../../generic/Button';
import { LabeledSelect, LabeledTextInput } from '../../generic/Forms';
import LabeledNumberInput from '../../generic/Forms/LabeledNumberInput';
import Text from '../../generic/Text';
import type { FailedFetchResponse } from '../../../helpers/errors';
import type { SelectOption } from '../../../types';

import { BILLING_TERM_URL } from '.';

import styles from './BillingTermCard.module.less';

const revisionOption = { label: 'Revision', value: BillingTermType.Revision };
const binaryOption = { label: 'Binary', value: BillingTermType.Binary };
const options = [revisionOption, binaryOption];

/**
 * Validates and returns a date object from a string
 * @param dateInput {string} possible date input
 * @returns {Date | null} date
 */
const getAndValidateDate = (dateInput: string): Date | null => {
  const date = new Date(dateInput);

  if (Number.isNaN(date.valueOf())) {
    return null;
  }

  return date;
};

type Props = {
  onTermAdd: () => void;
};

const AddBillingTermForm: VoidFunctionComponent<Props> = ({ onTermAdd }: Props) => {
  const isPerRevisionPlan = window.user?.organization?.stripe_plan === PREMIUM_PER_REVISION_PLAN_NAME;
  const isBinaryDecompEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);

  const { status, onError, onPending, onSuccess } = useAsyncStatus();
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [revisionLimit, setRevisionLimit] = useState<number>(0);
  const [errors, setErrors] = useState<string[]>([]);
  const [type, setType] = useState<SelectOption<BillingTermType>>(
    // This card only shows if the org is on the per revision plan OR has binary decomp enabled.
    // Default to revision is per revision plan is enabled, otherwise default to binary.
    isPerRevisionPlan ? revisionOption : binaryOption,
  );

  const onSubmit = async () => {
    const submitErrors = [];
    const termStart = getAndValidateDate(startDate);
    const termEnd = getAndValidateDate(endDate);

    // Validate
    if (!termStart) {
      submitErrors.push('Invalid start date');
    }

    if (!termEnd) {
      submitErrors.push('Invalid end date');
    }

    if (revisionLimit <= 0) {
      submitErrors.push('Revision limit must be greater than 0');
    }

    if (termStart && termEnd && termStart > termEnd) {
      submitErrors.push('Start date cannot be after end date');
    }

    setErrors(submitErrors);
    if (submitErrors.length > 0) {
      return;
    }

    onPending();

    const billingTermRequest = {
      termStart,
      termEnd,
      quantity: revisionLimit,
      type: type.value,
    };

    try {
      await api.post(BILLING_TERM_URL, billingTermRequest);
    } catch (err: unknown) {
      setErrors(errorMessagesListFromResponse(err as FailedFetchResponse));
      onError(err as FailedFetchResponse);
      return;
    }

    onTermAdd();
    setStartDate('');
    setEndDate('');
    setRevisionLimit(0);
    onSuccess();
  };

  return (
    <div className={cx(styles.addTerm)}>
      <Text.H4>Add a new Billing Term</Text.H4>
      {errors && errors.length !== 0 && errors.map((err) => <div className="alert alert-error">{err}</div>)}
      <div className={cx(styles.dateInput)}>
        <LabeledTextInput
          label="Start Date"
          placeholder="Start Date"
          type="date"
          value={startDate}
          onChange={setStartDate}
          size="small"
        />
        <span />
        <LabeledTextInput
          label="End Date"
          placeholder="End Date"
          type="date"
          value={endDate}
          onChange={setEndDate}
          size="small"
        />
      </div>
      <div className={cx(styles.quantityInput)}>
        <LabeledNumberInput
          label={`${_.capitalize(type.value)} limit`}
          value={revisionLimit}
          min={1}
          onChange={(val) => setRevisionLimit(parseInt(val, 10) || 0)}
          size="small"
        />
        {isPerRevisionPlan && isBinaryDecompEnabled && (
          <LabeledSelect
            id="billing-term-type"
            label="Billing Term Type"
            options={options}
            value={type}
            onChange={(val) => setType(val as SelectOption<BillingTermType>)}
          />
        )}
        <LoaderButton
          type={BUTTON_TYPE.PRIMARY}
          size={BUTTON_SIZE.LARGE}
          title="Add term"
          isLoading={status.isLoading}
          isDisabled={status.isLoading}
          onClick={onSubmit}
        >
          Add Term
        </LoaderButton>
      </div>
    </div>
  );
};

export default AddBillingTermForm;
